import React, { useState, useEffect } from 'react';
import './QuoteRequestPage.css';
import { getQuotes } from '../../services/quoteService';
import { Quote } from '../../types/quote';
import ComingSoonAction from '../../compoonents/ComingSoonAction/ComingSoonAction';
import { FiMail, FiDownload } from 'react-icons/fi';
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from '../../compoonents/PageToolbar';
import PageLoader from '../../compoonents/PageLoader';

// --- ICON IMPORTS ---
import emptyQuoteIcon from '../../assets/images/quote-icon.png';
import prevIcon from '../../assets/images/previcon.png';
import nextIcon from '../../assets/images/nexticon.png';

const QuoteRequestPage: React.FC = () => {
  // Data State
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterService, setFilterService] = useState(''); // e.g., 'import', 'export', 'procurement'

  const QUOTE_FILTER_OPTIONS = [
    { value: 'import', label: 'Import' },
    { value: 'export', label: 'Export' },
    { value: 'procurement', label: 'Procurement' },
  ];

  // 2. Fetch Data (Debounced)
  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass params to backend
        const response = await getQuotes(currentPage, searchQuery, filterService);
        setQuotes(response.data?.quotes || []);
        setTotalPages(response.data?.pagination?.totalPages ?? 1);
        setTotalItems(response.data?.pagination?.totalItems ?? 0);
      } catch (err) {
        setError('Failed to load quote requests.');
      } finally {
        setLoading(false);
      }
    };

    // Debounce to prevent flashing on every keystroke
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPage, searchQuery, filterService]);

  // 3. Client-Side Filtering (Fallback if backend doesn't filter)
  const visibleQuotes = quotes.filter((quote) => {
    // Search Filter (ID or Email)
    const lowerSearch = searchQuery.toLowerCase();
    const matchesSearch = 
      quote.id.toLowerCase().includes(lowerSearch) || 
      quote.email.toLowerCase().includes(lowerSearch);

    // Dropdown Service Type Filter
    let matchesType = true;
    if (filterService) {
      // Check if the quote service type includes the filter (e.g. "Import" matches "Sea Import")
      matchesType = (quote.serviceType || '').toLowerCase().includes(filterService.toLowerCase());
    }

    return matchesSearch && matchesType;
  });

  // Handlers
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };

  const handleFilterChange = (type: string) => {
    setFilterService(type);
    setCurrentPage(1);
  };

  return (
    <div className="quote-request-page">
      {/* HEADER - Always Visible */}
      <header className="page-header">
        <h3>All Quote Requests</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterService}
              options={QUOTE_FILTER_OPTIONS}
              onChange={handleFilterChange}
              allLabel="All Services"
            />
            <PageSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by Request ID, email..."
            />
          </PageToolbar>
        </div>
      </header>

      {/* ERROR MESSAGE */}
      {error && <div className="page-error">{error}</div>}

      {/* LOADING OVERLAY */}
      {loading && !error && (
        <PageLoader message="Loading quote requests..." />
      )}

      {/* CONTENT */}
      {!loading && !error && (
        <>
           {visibleQuotes.length === 0 ? (
            <div className="quote-request-page page--empty">
               {/* Check if empty due to search or actually empty */}
               {searchQuery || filterService ? (
                 <div style={{textAlign: 'center', padding: '40px'}}>
                   <h3>No quotes found matching "{searchQuery}"</h3>
                 </div>
               ) : (
                 <>
                  <img src={emptyQuoteIcon} alt="No quote requests" />
                  <h3>No quote requests have been made yet</h3>
                 </>
               )}
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Email</th>
                    <th>Service</th>
                    <th>Location From</th>
                    <th>Location To</th>
                    <th>Goods Type</th>
                    <th>Weight</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQuotes.map((quote) => (
                    <tr key={quote.id}>
                      <td data-label="Request ID">{quote.id.substring(0, 8)}...</td>
                      <td data-label="Email">{quote.email}</td>
                      <td data-label="Service">{quote.serviceType}</td>
                      <td data-label="Location From">{quote.locationFrom}</td>
                      <td data-label="Location To">{quote.locationTo}</td>
                      <td data-label="Goods Type">{quote.goodsType}</td>
                      <td data-label="Weight">{`${quote.weight}kg`}</td>
                      <td data-label="Date">{new Date(quote.createdAt).toLocaleDateString()}</td>
                      <td data-label="Action">
                        <div className="table-action-buttons">
                          <ComingSoonAction title="Email quote — coming soon">
                            <button
                              type="button"
                              className="table-action-btn"
                              aria-label="Email quote"
                              disabled
                            >
                              <FiMail />
                            </button>
                          </ComingSoonAction>
                          <ComingSoonAction title="Download quote — coming soon">
                            <button
                              type="button"
                              className="table-action-btn"
                              aria-label="Download quote"
                              disabled
                            >
                              <FiDownload />
                            </button>
                          </ComingSoonAction>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {visibleQuotes.length > 0 && (
            <footer className="page-footer">
              <div className="pagination-info">Showing page {currentPage} of {totalPages} ({totalItems} items)</div>
              <div className="pagination-controls">
                <button onClick={handlePrevPage} disabled={currentPage === 1}><img src={prevIcon} alt="Previous" /></button>
                <button onClick={handleNextPage} disabled={currentPage >= totalPages}><img src={nextIcon} alt="Next" /></button>
              </div>
            </footer>
          )}
        </>
      )}
    </div>
  );
};

export default QuoteRequestPage;